import React from 'react';
import { CAREER_PASSPORTS } from '../../config/careerPassportAssets';

export { CAREER_PASSPORTS } from '../../config/careerPassportAssets';

// Interests Assessment Step Component
export const InterestsAssessmentStep = ({ careerFields, assessmentData, onInterestChange, onPassportInterestChange }) => {
  const getInterestLevel = (fieldId) => {
    return assessmentData.interests?.career_field_interests?.[fieldId] || null;
  };

  const getPassportInterestLevel = (passportId) => {
    return assessmentData.interests?.passport_interests?.[passportId] || null;
  };

  const interestLabels = {
    1: 'Not Interested',
    2: 'Slightly Interested', 
    3: 'Moderately Interested',
    4: 'Very Interested',
    5: 'Extremely Interested'
  };

  return (
    <div className="interests-assessment-step">
      <h2>💡 Career Interests</h2>
      <p>Choose the Passport pathways that best reflect the skills and opportunities you want to explore.</p>

      <section className="passport-interest-section" aria-labelledby="passport-interest-heading">
        <div className="passport-section-heading">
          <div>
            <span className="passport-eyebrow">Nasi Spani pathways</span>
            <h3 id="passport-interest-heading">Which Passports interest you?</h3>
            <p>Rate each pathway from 1 to 5. Your selections help shape your career and training recommendations.</p>
          </div>
          <span className="passport-selection-note">
            {Object.keys(assessmentData.interests?.passport_interests || {}).length} rated
          </span>
        </div>

        <div className="passport-grid">
          {CAREER_PASSPORTS.map(passport => {
            const interestLevel = getPassportInterestLevel(passport.id);

            return (
              <article
                key={passport.id}
                className={`passport-card passport-card--${passport.tone} ${interestLevel ? 'is-rated' : ''}`}
              >
                <div className="passport-cover" aria-hidden="true">
                  <img src={passport.cover} alt="" loading="lazy" />
                  <div className="passport-cover-copy">
                    <span>CAREER PASSPORT</span>
                    <strong>{passport.title}</strong>
                    <small>{passport.nqfLevels}</small>
                  </div>
                </div>

                <div className="passport-card-body">
                  <div className="passport-card-title-row">
                    <h4>{passport.title}</h4>
                    {interestLevel && <span className="passport-rated-badge">Rated</span>}
                  </div>
                  <p className="passport-description">{passport.description}</p>
                  <p className="passport-coverage"><strong>Covers:</strong> {passport.coverage}</p>

                  <div className="passport-interest-selector">
                    <span className="passport-interest-label">Interest level</span>
                    <div className="passport-interest-scale" role="group" aria-label={`${passport.title} interest level`}>
                      {[1, 2, 3, 4, 5].map(level => (
                        <button
                          key={level}
                          type="button"
                          className={`passport-interest-btn ${interestLevel === level ? 'active' : ''}`}
                          onClick={() => onPassportInterestChange(passport.id, level)}
                          aria-pressed={interestLevel === level}
                          aria-label={`${level} out of 5: ${interestLabels[level]}`}
                        >
                          {level}
                        </button>
                      ))}
                    </div>
                    <small>{interestLevel ? interestLabels[interestLevel] : 'Select a rating'}</small>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <details className="career-fields-refinement">
        <summary>Optional: refine your interests by specific career field</summary>
        <p className="career-fields-refinement-copy">Passports provide the main pathway signal. Use these detailed fields if you already have a specific industry in mind.</p>
        <div className="career-fields-grid">
          {careerFields.map(field => (
            <div key={field.id} className="career-field-card">
              <div className="field-header">
                <span className="field-icon">{field.icon}</span>
                <h3>{field.name}</h3>
              </div>
              <p>{field.description}</p>

              <div className="growth-indicator">
                <span className={`growth-badge ${field.growth_outlook}`}>
                  {field.growth_outlook.replace('_', ' ')} Growth
                </span>
                <span className="salary-range">{field.average_salary_range}</span>
              </div>

              <div className="interest-selector">
                <label>Interest Level:</label>
                <div className="interest-scale">
                  {[1, 2, 3, 4, 5].map(level => (
                    <button
                      key={level}
                      type="button"
                      className={`interest-btn ${getInterestLevel(field.id) === level ? 'active' : ''}`}
                      onClick={() => onInterestChange(field.id, level)}
                      title={interestLabels[level]}
                    >
                      {level}
                    </button>
                  ))}
                </div>
                <small>{getInterestLevel(field.id) ? interestLabels[getInterestLevel(field.id)] : 'Select a rating'}</small>
              </div>
            </div>
          ))}
        </div>
      </details>
    </div>
  );
};

// Preferences Assessment Step Component
export const PreferencesAssessmentStep = ({ assessmentData, onPreferenceChange }) => {
  const studyFormatOptions = [
    { value: 'full_time', label: 'Full-time' },
    { value: 'part_time', label: 'Part-time' },
    { value: 'evening', label: 'Evening classes' },
    { value: 'weekend', label: 'Weekend classes' },
    { value: 'online', label: 'Online learning' },
    { value: 'hybrid', label: 'Hybrid (online + in-person)' }
  ];

  const incomeRanges = [
    { value: '0-5000', label: 'R0 - R5,000' },
    { value: '5000-10000', label: 'R5,000 - R10,000' },
    { value: '10000-15000', label: 'R10,000 - R15,000' },
    { value: '15000-25000', label: 'R15,000 - R25,000' },
    { value: '25000-35000', label: 'R25,000 - R35,000' },
    { value: '35000+', label: 'R35,000+' }
  ];

  const targetIncomeRanges = [
    { value: '10000-15000', label: 'R10,000 - R15,000' },
    { value: '15000-25000', label: 'R15,000 - R25,000' },
    { value: '25000-35000', label: 'R25,000 - R35,000' },
    { value: '35000-50000', label: 'R35,000 - R50,000' },
    { value: '50000+', label: 'R50,000+' }
  ];

  return (
    <div className="preferences-assessment-step">
      <h2>⚙️ Study & Career Preferences</h2>
      <p>Help us understand your practical constraints and career goals.</p>

      <div className="preferences-grid">
        <div className="preference-section">
          <h3>📅 Study Preferences</h3>
          
          <div className="form-group">
            <label>Preferred Study Format:</label>
            <select
              value={assessmentData.interests?.preferred_study_format || ''}
              onChange={(e) => onPreferenceChange('preferred_study_format', e.target.value)}
            >
              <option value="">Select format...</option>
              {studyFormatOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Available Study Hours per Week:</label>
            <input
              type="number"
              min="1"
              max="168"
              value={assessmentData.interests?.available_study_hours_per_week || ''}
              onChange={(e) => onPreferenceChange('available_study_hours_per_week', parseInt(e.target.value))}
              placeholder="e.g., 20"
            />
            <small>Consider work, family, and other commitments</small>
          </div>
        </div>

        <div className="preference-section">
          <h3>📍 Location Preferences</h3>
          
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={assessmentData.interests?.willing_to_relocate || false}
                onChange={(e) => onPreferenceChange('willing_to_relocate', e.target.checked)}
              />
              I'm willing to relocate for training
            </label>
          </div>

          <div className="form-group">
            <label>Maximum Travel Distance (km):</label>
            <input
              type="number"
              min="0"
              max="1000"
              value={assessmentData.interests?.max_travel_distance_km || ''}
              onChange={(e) => onPreferenceChange('max_travel_distance_km', parseInt(e.target.value))}
              placeholder="e.g., 50"
            />
            <small>Daily commute distance you're comfortable with</small>
          </div>
        </div>

        <div className="preference-section">
          <h3>💰 Financial Goals</h3>
          
          <div className="form-group">
            <label>Current Income Range:</label>
            <select
              value={assessmentData.interests?.current_income_range || ''}
              onChange={(e) => onPreferenceChange('current_income_range', e.target.value)}
            >
              <option value="">Select range...</option>
              {incomeRanges.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Target Income Range:</label>
            <select
              value={assessmentData.interests?.target_income_range || ''}
              onChange={(e) => onPreferenceChange('target_income_range', e.target.value)}
            >
              <option value="">Select target...</option>
              {targetIncomeRanges.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="preference-section">
          <h3>🏢 Work Environment</h3>
          
          <div className="work-environment-options">
            <label>
              <input
                type="checkbox"
                checked={assessmentData.interests?.work_environment_preferences?.remote_work || false}
                onChange={(e) => onPreferenceChange('work_environment_preferences', {
                  ...assessmentData.interests?.work_environment_preferences,
                  remote_work: e.target.checked
                })}
              />
              Remote work opportunities
            </label>

            <label>
              <input
                type="checkbox"
                checked={assessmentData.interests?.work_environment_preferences?.team_environment || false}
                onChange={(e) => onPreferenceChange('work_environment_preferences', {
                  ...assessmentData.interests?.work_environment_preferences,
                  team_environment: e.target.checked
                })}
              />
              Team-based environment
            </label>

            <label>
              <input
                type="checkbox"
                checked={assessmentData.interests?.work_environment_preferences?.leadership_opportunities || false}
                onChange={(e) => onPreferenceChange('work_environment_preferences', {
                  ...assessmentData.interests?.work_environment_preferences,
                  leadership_opportunities: e.target.checked
                })}
              />
              Leadership opportunities
            </label>

            <label>
              <input
                type="checkbox"
                checked={assessmentData.interests?.work_environment_preferences?.creative_work || false}
                onChange={(e) => onPreferenceChange('work_environment_preferences', {
                  ...assessmentData.interests?.work_environment_preferences,
                  creative_work: e.target.checked
                })}
              />
              Creative and innovative work
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};

// Review Assessment Step Component
export const ReviewAssessmentStep = ({ assessmentData, skillCategories, careerFields }) => {
  const getSkillsByCategory = () => {
    const skillsByCategory = {};
    assessmentData.skills.forEach(userSkill => {
      const category = skillCategories.find(cat => 
        cat.skills?.some(skill => skill.id === userSkill.skill_id)
      );
      if (category) {
        if (!skillsByCategory[category.name]) {
          skillsByCategory[category.name] = [];
        }
        const skill = category.skills.find(s => s.id === userSkill.skill_id);
        skillsByCategory[category.name].push({
          ...skill,
          ...userSkill
        });
      }
    });
    return skillsByCategory;
  };

  const getInterestedFields = () => {
    const interests = assessmentData.interests?.career_field_interests || {};
    return careerFields.filter(field => interests[field.id] >= 4);
  };

  const getInterestedPassports = () => {
    const interests = assessmentData.interests?.passport_interests || {};
    return CAREER_PASSPORTS.filter(passport => interests[passport.id]);
  };

  const proficiencyLabels = {
    1: 'Beginner',
    2: 'Basic',
    3: 'Intermediate', 
    4: 'Advanced',
    5: 'Expert'
  };

  return (
    <div className="review-assessment-step">
      <h2>📋 Review Your Assessment</h2>
      <p>Please review your responses before submitting. You can go back to make changes if needed.</p>

      <div className="review-sections">
        <div className="review-section">
          <h3>📚 Your Skills</h3>
          {Object.entries(getSkillsByCategory()).map(([categoryName, skills]) => (
            <div key={categoryName} className="skills-category-review">
              <h4>{categoryName}</h4>
              <div className="skills-list">
                {skills.map(skill => (
                  <div key={skill.id} className="skill-review-item">
                    <span className="skill-name">{skill.name}</span>
                    <span className="skill-proficiency">
                      {proficiencyLabels[skill.proficiency_level]}
                    </span>
                    {skill.years_experience > 0 && (
                      <span className="skill-experience">
                        {skill.years_experience} years
                      </span>
                    )}
                    {skill.is_certified && (
                      <span className="skill-certified">✓ Certified</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="review-section">
          <h3>💡 Career Interests</h3>
          <div className="review-passports">
            {getInterestedPassports().map(passport => (
              <div key={passport.id} className={`review-passport review-passport--${passport.tone}`}>
                <img src={passport.cover} alt="" />
                <div>
                  <strong>{passport.title}</strong>
                  <span>{assessmentData.interests.passport_interests[passport.id]}/5 interest</span>
                </div>
              </div>
            ))}
          </div>
          {getInterestedPassports().length === 0 && (
            <p className="review-empty">No Passport pathways rated yet.</p>
          )}
          <div className="interested-fields">
            {getInterestedFields().map(field => (
              <div key={field.id} className="interested-field">
                <span className="field-icon">{field.icon}</span>
                <span className="field-name">{field.name}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="review-section">
          <h3>⚙️ Preferences</h3>
          <div className="preferences-summary">
            {assessmentData.interests?.preferred_study_format && (
              <div className="preference-item">
                <strong>Study Format:</strong> {assessmentData.interests.preferred_study_format.replace('_', ' ')}
              </div>
            )}
            {assessmentData.interests?.available_study_hours_per_week && (
              <div className="preference-item">
                <strong>Study Hours:</strong> {assessmentData.interests.available_study_hours_per_week} hours/week
              </div>
            )}
            {assessmentData.interests?.target_income_range && (
              <div className="preference-item">
                <strong>Target Income:</strong> {assessmentData.interests.target_income_range.replace('-', ' - R')}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="assessment-summary">
        <div className="summary-stats">
          <div className="stat-item">
            <span className="stat-number">{assessmentData.skills.length}</span>
            <span className="stat-label">Skills Assessed</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">{getInterestedPassports().length}</span>
            <span className="stat-label">Passports Rated</span>
          </div>
        </div>
      </div>
    </div>
  );
};
